import { findIndex, forEach } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useWindowSize } from 'react-use';

function getWindowWidth() {
  return (
    global.window?.innerWidth || global.document?.documentElement?.clientWidth || global.document?.body?.clientWidth
  );
}

function useWindow(effect, deps) {
  const [width, setWidth] = useState(getWindowWidth());

  useEffect(() => {
    const onResize = () => {
      if (getWindowWidth() !== width) {
        setWidth(getWindowWidth());
      }
    };

    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [width]);
  useEffect(() => {
    if (effect) effect(width);
    // eslint-disable-next-line
  }, [width, ...(deps || [])]);
}

/**
 * @example
 * const pb = useParseBreakpoint();
 * pb({xs: 'value of xs', sm: '...', 'md': <span>Or component</span>, ...})
 * @returns {(function(*=): (any|undefined))|*}
 */
export function useParseBreakpoint() {
  const [breakpoints] = useState([
    ['xs', 0],
    ['mxs', 360],
    ['sm', 600],
    ['md', 840],
    ['lg', 1280],
    ['xl', 1440],
    ['xxl', 1920],
  ]);
  const { width } = useWindowSize();

  return useCallback(
    (value) => {
      breakpoints.sort((a, b) => b[1] - a[1]);

      const sortedBreakpoints = breakpoints.map((v) => v[0]).reverse();
      let breakpoint;

      forEach(breakpoints, (v) => {
        if (v[1] <= width) {
          [breakpoint] = v;

          return false;
        }

        return undefined;
      });

      if (typeof value !== 'object') {
        return value;
      }

      if (value[breakpoint] === undefined) {
        const index = findIndex(sortedBreakpoints, (v) => v === breakpoint);

        for (let i = index; i >= 0; i -= 1) {
          if (value[sortedBreakpoints[i]] !== undefined) {
            return value[sortedBreakpoints[i]];
          }
        }
      } else return value[breakpoint];

      return undefined;
    },
    [width, breakpoints],
  );
}

function useWindowScroll() {
  const [state, setState] = useState({
    x: window.pageXOffset,
    y: window.pageYOffset,
  });

  useEffect(() => {
    const handle = () => {
      const { pageXOffset, pageYOffset } = window;

      setState({
        x: pageXOffset,
        y: pageYOffset,
      });
    };

    window.addEventListener('scroll', handle, {
      passive: true,
      capture: false,
    });

    return () => {
      window.removeEventListener('scroll', handle);
    };
  }, []);

  return state;
}
